import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  A2PRegistrationInfoInterface,
  A2PRegistrationStatusInterface,
  AuthorizedRepresentativeInterface,
  BrandStatus,
  BusinessInterface,
  CampaignInfoInterface,
  DataField,
  UpsertA2PRegistrationInfoRequestInterface,
  ValidationStatus,
} from '@vendasta/smsv2';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { createPhoneInputFormGroup } from '../../../components/phone-input/sms-phone-input.component';
import { BrandFormControls, CampaignFormControlsV2, Position } from '../sms-registration-form/types';
import {
  DATA_FIELD_TO_FORM_FIELD,
  EIN_REGEX,
  HTTPS_URL_REGEX,
  IDENTIFIER_TYPE_EIN,
  RegistrationStage,
  URL_REGEX,
  ZIP_CODE_REGEX,
} from '../a2p-registration.constants';
import { createGroupValueChangeValidator } from '../a2p-helper-functions';

const DEFAULT_VERBAL_CONSENT_SCRIPT_KEY = 'SMS_REGISTRATION.V2.CAMPAIGN.VERBAL_CONSENT_DEFAULT_SCRIPT';

const FIELD_TO_STEP = new Map<string, number>([
  ['businessName', 0],
  ['businessCategory', 0],
  ['businessType', 0],
  ['regionOfOperation', 0],
  ['businessRegistrationNumber', 0],
  ['address', 0],
  ['additionalAddressLine', 0],
  ['state', 0],
  ['city', 0],
  ['zipCode', 0],
  ['website', 0],
  ['firstName', 0],
  ['lastName', 0],
  ['jobPosition', 0],
  ['businessTitle', 0],
  ['email', 0],
  ['phoneNumber', 0],
  ['country', 0],
  ['smsUsage', 1],
  ['collectsVerbalConsent', 1],
  ['verbalConsentScript', 1],
  ['hasWebchatWidget', 1],
  ['webchatWidgetUrl', 1],
  ['hasWebForm', 1],
  ['webFormUrl', 1],
  ['privacyPolicyUrl', 1],
  ['termsAndConditionsUrl', 1],
]);

export class BrandForm extends FormGroup<BrandFormControls> {
  constructor() {
    super({
      businessName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(128)],
      }),
      businessCategory: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      businessType: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      regionOfOperation: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      businessRegistrationNumber: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(EIN_REGEX)],
      }),
      address: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      additionalAddressLine: new FormControl('', { nonNullable: true }),
      state: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      city: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      zipCode: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(ZIP_CODE_REGEX)],
      }),
      website: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      firstName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      lastName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      jobPosition: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      businessTitle: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
      phoneNumber: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    });
  }

  prefill(info: A2PRegistrationInfoInterface): void {
    const biz = info?.business;
    const rep = info?.authorizedRepresentative;
    this.patchValue({
      businessName: biz?.legalName ?? '',
      businessCategory: biz?.industry ?? '',
      businessType: biz?.businessType ?? '',
      regionOfOperation: biz?.regionOfOperation ?? '',
      businessRegistrationNumber: biz?.registrationIdentifier?.identifier ?? '',
      address: biz?.streetAddress ?? '',
      additionalAddressLine: biz?.streetAddressSecondary ?? '',
      state: biz?.state ?? '',
      city: biz?.city ?? '',
      zipCode: biz?.postalCode ?? '',
      website: biz?.websiteUrl ?? '',
      firstName: rep?.firstName ?? '',
      lastName: rep?.lastName ?? '',
      jobPosition: rep?.jobPosition ?? '',
      businessTitle: rep?.businessTitle ?? '',
      email: rep?.email ?? '',
      phoneNumber: rep?.phoneNumber ?? '',
    });
  }

  toBusiness(): BusinessInterface {
    const v = this.getRawValue();
    return {
      legalName: v.businessName,
      businessType: v.businessType,
      industry: v.businessCategory,
      registrationIdentifier: {
        identifierType: IDENTIFIER_TYPE_EIN,
        identifier: v.businessRegistrationNumber,
      },
      regionOfOperation: v.regionOfOperation,
      websiteUrl: v.website,
      socialMediaProfileUrls: [],
      streetAddress: v.address,
      streetAddressSecondary: v.additionalAddressLine,
      city: v.city,
      state: v.state,
      postalCode: v.zipCode,
      country: 'US',
    } satisfies BusinessInterface;
  }

  toAuthorizedRepresentative(): AuthorizedRepresentativeInterface {
    const v = this.getRawValue();
    return {
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.email,
      phoneNumber: v.phoneNumber,
      businessTitle: v.businessTitle,
      jobPosition: v.jobPosition,
    } satisfies AuthorizedRepresentativeInterface;
  }

  getInitialValues(): Map<string, string> {
    return new Map(Object.entries(this.getRawValue()).map(([k, v]) => [k, v ?? '']));
  }
}

export class CampaignForm extends FormGroup<CampaignFormControlsV2> {
  constructor() {
    super({
      smsUsage: new FormControl<string>('both', { nonNullable: true, validators: [Validators.required] }),
      collectsVerbalConsent: new FormControl(true, { nonNullable: true }),
      verbalConsentScript: new FormControl('', { nonNullable: true }),
      hasWebchatWidget: new FormControl(true, { nonNullable: true }),
      webchatWidgetUrl: new FormControl('', { nonNullable: true, validators: [Validators.pattern(URL_REGEX)] }),
      hasWebForm: new FormControl(true, { nonNullable: true }),
      webFormUrl: new FormControl('', { nonNullable: true, validators: [Validators.pattern(URL_REGEX)] }),
      privacyPolicyUrl: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(HTTPS_URL_REGEX)],
      }),
      termsAndConditionsUrl: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(HTTPS_URL_REGEX)],
      }),
    });
  }

  prefill(info: A2PRegistrationInfoInterface, translateService: TranslateService): void {
    const campaign = info?.campaignInfo;
    const defaultScript = translateService.instant(DEFAULT_VERBAL_CONSENT_SCRIPT_KEY);
    this.patchValue({
      privacyPolicyUrl: campaign?.privacyPolicyUrl ?? '',
      termsAndConditionsUrl: campaign?.termsAndConditionsUrl ?? '',
      smsUsage: campaign?.smsUsage ?? 'both',
      collectsVerbalConsent: campaign?.collectsVerbalConsent ?? true,
      verbalConsentScript: campaign?.verbalConsentScript || defaultScript,
      hasWebchatWidget: campaign?.hasWebchatWidget ?? true,
      webchatWidgetUrl: campaign?.webchatWidgetUrl ?? '',
      hasWebForm: campaign?.hasWebForm ?? true,
      webFormUrl: campaign?.webFormUrl ?? '',
    });
  }

  toCampaignInfo(): CampaignInfoInterface {
    const v = this.getRawValue();
    return {
      privacyPolicyUrl: v.privacyPolicyUrl,
      termsAndConditionsUrl: v.termsAndConditionsUrl,
      smsUsage: v.smsUsage,
      collectsVerbalConsent: v.collectsVerbalConsent,
      verbalConsentScript: v.verbalConsentScript,
      hasWebchatWidget: v.hasWebchatWidget,
      webchatWidgetUrl: v.webchatWidgetUrl,
      hasWebForm: v.hasWebForm,
      webFormUrl: v.webFormUrl,
    };
  }

  setupCrossFieldValidation(destroyRef: DestroyRef): void {
    this.setupConditionalRequired('collectsVerbalConsent', 'verbalConsentScript', destroyRef);
    this.setupConditionalRequired('hasWebchatWidget', 'webchatWidgetUrl', destroyRef);
    this.setupConditionalRequired('hasWebForm', 'webFormUrl', destroyRef);
  }

  private setupConditionalRequired(
    toggleField: keyof CampaignFormControlsV2,
    targetField: keyof CampaignFormControlsV2,
    destroyRef: DestroyRef,
  ): void {
    const applyValidator = (value: boolean) => {
      const control = this.controls[targetField];
      if (value) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
      control.updateValueAndValidity();
    };

    const toggleControl = this.controls[toggleField] as FormControl<boolean>;
    applyValidator(toggleControl.value);
    toggleControl.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(applyValidator);
  }
}

export class RegistrationForm extends FormGroup<{ brand: BrandForm; campaign: CampaignForm }> {
  readonly brand: BrandForm;
  readonly campaign: CampaignForm;
  readonly phoneInput = createPhoneInputFormGroup(undefined, false);

  private registrationStage: RegistrationStage = RegistrationStage.NotSubmitted;
  private initialValues = new Map<string, string>();
  private fieldsWithIssues: string[] = [];
  private dataErrorValidatorsApplied = false;
  private dataErrorValidator: ReturnType<typeof createGroupValueChangeValidator> | null = null;

  constructor() {
    const brand = new BrandForm();
    const campaign = new CampaignForm();
    super({ brand, campaign });
    this.brand = brand;
    this.campaign = campaign;
  }

  get allControls(): Record<string, FormControl> {
    return { ...this.brand.controls, ...this.campaign.controls, ...this.phoneInput.controls } as Record<
      string,
      FormControl
    >;
  }

  setupBehaviors(destroyRef: DestroyRef, translateService: TranslateService): void {
    this.campaign.setupCrossFieldValidation(destroyRef);
    this.setupJobPositionSync(destroyRef);
    this.setupFieldChangeTracking(destroyRef);
    this.setupPhoneInputSync(destroyRef);
    this.setupVerbalConsentDefault(translateService);
  }

  prefill(info: A2PRegistrationInfoInterface, translateService: TranslateService): void {
    this.brand.prefill(info);
    this.prefillPhoneInput(info?.authorizedRepresentative?.phoneNumber);
    this.campaign.prefill(info, translateService);
  }

  toUpsertRequest(submitted: boolean): UpsertA2PRegistrationInfoRequestInterface {
    return {
      business: this.brand.toBusiness(),
      campaignInfo: this.campaign.toCampaignInfo(),
      authorizedRepresentative: this.brand.toAuthorizedRepresentative(),
      submitted,
    };
  }

  hasFormatErrors(): boolean {
    const controls = this.allControls;
    return Object.keys(controls).some((key) => {
      const errors = controls[key].errors;
      if (errors === null) return false;
      return Object.keys(errors).some((errorKey) => errorKey !== 'required' && errorKey !== 'groupValueError');
    });
  }

  resetAndPrefill(info: A2PRegistrationInfoInterface, translateService: TranslateService): void {
    if (this.dataErrorValidatorsApplied && this.dataErrorValidator) {
      const controls = this.allControls;
      this.fieldsWithIssues.forEach((fieldName) => {
        controls[fieldName]?.removeValidators(this.dataErrorValidator!);
        controls[fieldName]?.updateValueAndValidity({ emitEvent: false });
      });
    }
    this.dataErrorValidator = null;
    this.reset();
    this.phoneInput.reset();
    this.registrationStage = RegistrationStage.NotSubmitted;
    this.initialValues = new Map<string, string>();
    this.fieldsWithIssues = [];
    this.dataErrorValidatorsApplied = false;
    this.prefill(info, translateService);
  }

  lockAll(): void {
    this.disable();
    this.phoneInput.disable();
  }

  unlockAll(): void {
    this.enable();
    this.phoneInput.enable();
  }

  lockBrandOnly(): void {
    this.enable();
    this.brand.disable();
    this.phoneInput.disable();
  }

  applyRegistrationStage(
    stage: RegistrationStage,
    info: A2PRegistrationInfoInterface,
    status: A2PRegistrationStatusInterface,
  ): { locked: boolean; campaignError: boolean } {
    this.registrationStage = stage;

    if (stage === RegistrationStage.CampaignError) {
      this.lockBrandOnly();
      return { locked: false, campaignError: true };
    }

    if (stage !== RegistrationStage.NotSubmitted && stage !== RegistrationStage.DataError) {
      this.lockAll();
      return { locked: true, campaignError: false };
    }

    this.unlockAll();

    if (stage === RegistrationStage.DataError) {
      this.initialValues = this.brand.getInitialValues();
      this.fieldsWithIssues = this.getFormFieldsWithIssues(status);
      const controls = this.allControls;
      this.fieldsWithIssues.forEach((fieldName) => {
        controls[fieldName]?.markAsTouched();
      });
      if (this.fieldsWithIssues.includes('phoneNumber')) {
        this.phoneInput.markAsTouched();
        this.phoneInput.controls.phoneNumber.markAsTouched();
      }
      if (!this.dataErrorValidatorsApplied) {
        this.dataErrorValidatorsApplied = true;
        this.dataErrorValidator = createGroupValueChangeValidator(
          this.fieldsWithIssues,
          this.initialValues,
          () => this.allControls,
        );
        this.fieldsWithIssues.forEach((fieldName) => {
          const control = controls[fieldName];
          if (control && this.initialValues.get(fieldName) !== undefined) {
            control.addValidators(this.dataErrorValidator!);
            control.updateValueAndValidity();
          }
        });
      }
    }

    return { locked: false, campaignError: false };
  }

  findFirstErrorStep(): number | undefined {
    const controls = this.allControls;
    for (const [fieldName, control] of Object.entries(controls)) {
      if (control.invalid) {
        return FIELD_TO_STEP.get(fieldName);
      }
    }
    return undefined;
  }

  private prefillPhoneInput(storedPhone: string | undefined): void {
    if (!storedPhone) return;
    const parsed = parsePhoneNumberFromString(storedPhone);
    if (parsed) {
      this.phoneInput.patchValue({
        country: parsed.country || 'US',
        phoneNumber: parsed.nationalNumber satisfies string,
      });
    } else {
      this.phoneInput.patchValue({ phoneNumber: storedPhone });
    }
  }

  private setupJobPositionSync(destroyRef: DestroyRef): void {
    const jobPositions = Object.values(Position);
    this.brand.controls.jobPosition.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe((value: string) => {
      const businessTitle = this.brand.controls.businessTitle.value;
      const titleIsPosition = jobPositions.some((p) => p === businessTitle);
      if (value !== Position.otherPosition && (businessTitle === '' || titleIsPosition)) {
        this.brand.controls.businessTitle.patchValue(value);
      } else if (value === Position.otherPosition && titleIsPosition) {
        this.brand.controls.businessTitle.patchValue('');
      }
    });
  }

  private setupFieldChangeTracking(destroyRef: DestroyRef): void {
    const controls = this.allControls;
    Object.keys(controls).forEach((key) => {
      controls[key].valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
        if (this.fieldsWithIssues.includes(key)) {
          this.fieldsWithIssues.forEach((fieldName) => {
            this.allControls[fieldName]?.updateValueAndValidity({ emitEvent: false });
          });
        }
      });
    });
  }

  private setupVerbalConsentDefault(translateService: TranslateService): void {
    const defaultScript = translateService.instant(DEFAULT_VERBAL_CONSENT_SCRIPT_KEY);
    if (!this.campaign.controls.verbalConsentScript.value) {
      this.campaign.controls.verbalConsentScript.setValue(defaultScript, { emitEvent: false });
    }
  }

  private setupPhoneInputSync(destroyRef: DestroyRef): void {
    this.phoneInput.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe((value) => {
      const country = (value.country || 'US') as CountryCode;
      const raw = value.phoneNumber || '';
      if (raw) {
        const parsed = parsePhoneNumberFromString(raw, country);
        this.brand.controls.phoneNumber.setValue(parsed ? parsed.formatInternational() : raw);
      } else {
        this.brand.controls.phoneNumber.setValue('');
      }
    });
  }

  private getFormFieldsWithIssues(registrationStatus: A2PRegistrationStatusInterface): string[] {
    if (!registrationStatus) return [];
    const result: string[] = [];

    const extractFields = (fieldsWithIssues: DataField[] | undefined) => {
      fieldsWithIssues?.forEach((field) => {
        const val = DATA_FIELD_TO_FORM_FIELD.get(field);
        if (val !== undefined) result.push(val);
      });
    };

    if (registrationStatus.secondaryBusinessProfile?.status === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT) {
      extractFields(registrationStatus.secondaryBusinessProfile.fieldsWithIssues);
    }
    if (registrationStatus.trustBundle?.status === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT) {
      extractFields(registrationStatus.trustBundle.fieldsWithIssues);
    }
    if (registrationStatus.brand?.status === BrandStatus.BRAND_STATUS_FAILED) {
      extractFields(registrationStatus.brand.fieldsWithIssues);
    }
    return result;
  }
}
